import {
    LeaderboardNameFormat,
    LeaderboardPreferences,
    LeaderboardPreferencesRequest,
    UserProfile,
} from "../models/userProfile";
import { updateProfile } from "./profileService";

/**
 * Venue leaderboard preferences (SCORZA-739).
 *
 * These live on the server-side profile, NOT in the device-local `ConfigPhone` settings: the
 * server is what gates submission and board membership, and it applies the under-18 age gate
 * against `dob`. Writing them anywhere else would let the two disagree.
 *
 * Participation is **on by default** for adults. Nothing here decides that — the server does, on
 * every submission — but the helpers below have to model it the same way, which is why the stored
 * field is `optOut` rather than `optIn`: a default-on flag whose absence meant "yes" reads as a
 * bug at every call site.
 */

/** Server rejection reasons we have specific copy for. */
export type OptInRejection = "ageGate" | "displayNameUnavailable" | "unknown";

/**
 * Reason codes from `refauth/features/venues/VenueOptInService.ts` (`OPT_IN_REJECTION`).
 * A refused change fails the whole PUT and comes back as HTTP 400 with a body of exactly
 * `{"errorMessage":"leaderboards:<code>"}` — there is no `errorDetail` key, because
 * `ResponseFactory` leaves it undefined and `res.json` drops it.
 *
 * The `leaderboards:` prefix is load-bearing: `ProfileService.updateProfile` also rejects with
 * unrelated plain strings that land as 400 + `errorMessage` ("Customer not found", "Failed to
 * update customer <username>", ...). Matching on the prefix is what stops those being reported
 * to the user as an age problem.
 */
const REJECTION_CODES: Record<string, OptInRejection> = {
    // Fails closed: an absent, unparseable or nonsensical dob is treated as under age.
    dobRequired: "ageGate",
    ageRestricted: "ageGate",
    // No first name on the profile to derive a public display name from. Not reachable for the
    // `anonymous` format, which needs no name.
    displayNameUnavailable: "displayNameUnavailable",
    invalidShape: "unknown",
    invalidOptOut: "unknown",
    invalidNameFormat: "unknown",
};

/** Longest display name the server will store (`DISPLAY_NAME_MAX_LENGTH`). */
export const DISPLAY_NAME_MAX_LENGTH = 40;

/** What the `anonymous` format renders (`ANONYMOUS_DISPLAY_NAME` in `VenueConfig.ts`). */
export const ANONYMOUS_DISPLAY_NAME = "Anonymous";

/** Minimum age to appear on a board (`MIN_LEADERBOARD_AGE_YEARS`). Under-18s are excluded
 *  outright and cannot turn it on. */
export const MIN_LEADERBOARD_AGE_YEARS = 18;

export const DEFAULT_NAME_FORMAT: LeaderboardNameFormat =
    "firstNameLastInitial";

/** In the order the settings select offers them: most identifying first. */
export const NAME_FORMATS: LeaderboardNameFormat[] = [
    "firstNameLastInitial",
    "initials",
    "anonymous",
];

/**
 * The public display name for a profile in a given format.
 *
 *   firstNameLastInitial  "Luke G"
 *   initials              "LG"
 *   anonymous             "Anonymous"
 *
 * The server derives this itself and ignores whatever the client sends — the client picks a
 * *shape*, never a string, because a free-text name on an unmoderated public board is an
 * impersonation vector. So this must stay in step with `deriveDisplayName` in
 * `refauth/features/venues/VenueOptInService.ts`; it is a preview of what the server will store.
 * Empty means the profile has no first name in a format that needs one, which the server refuses
 * rather than invent a placeholder for.
 *
 * Yes, this duplicates the server's algorithm. It is kept deliberately: the settings select has
 * to label each option with the name it would actually produce, and a user should not have to
 * pick an option to find out what it publishes. The stored value wins once it exists (see
 * `getLeaderboardPreferences`), so the blast radius of drift is a slightly stale preview, and the
 * algorithm is pinned by tests.
 *
 * If the duplication ever needs removing, the fix is for refauth to return the derived previews
 * from `ProfileService.getProfile` (a few lines, after the view row loads, using the real
 * `deriveDisplayName`). Do NOT derive them inside the `customers/summarise/profile` map
 * function: Couch map functions are ES5 with no imports, so that would add a *third* copy of the
 * algorithm and make every future tweak a reindex.
 */
export function deriveDisplayName(
    profile?: UserProfile,
    format: LeaderboardNameFormat = DEFAULT_NAME_FORMAT
): string {
    if (format === "anonymous") {
        return ANONYMOUS_DISPLAY_NAME;
    }

    const firstName = (profile?.firstName || "").replace(/\s+/g, " ").trim();
    const lastName = (profile?.lastName || "").replace(/\s+/g, " ").trim();

    if (!firstName) {
        return "";
    }
    const initial = lastName ? lastName.charAt(0).toUpperCase() : "";

    if (format === "initials") {
        return (firstName.charAt(0).toUpperCase() + initial).slice(
            0,
            DISPLAY_NAME_MAX_LENGTH
        );
    }

    const name = initial ? firstName + " " + initial : firstName;
    return name.slice(0, DISPLAY_NAME_MAX_LENGTH);
}

/** The name a profile gets when it has never chosen a format. */
export function defaultDisplayName(profile?: UserProfile): string {
    return deriveDisplayName(profile, DEFAULT_NAME_FORMAT);
}

/** Every format with the name it would actually publish, for the settings select. */
export function nameFormatOptions(
    profile?: UserProfile
): { format: LeaderboardNameFormat; displayName: string }[] {
    return NAME_FORMATS.map((format) => ({
        format,
        displayName: deriveDisplayName(profile, format),
    }));
}

/**
 * Current preferences, with the derived display name filled in when none is stored yet — which
 * is the normal case, since a user who has never touched the setting has no stored value and is
 * nonetheless on the boards.
 */
export function getLeaderboardPreferences(
    profile?: UserProfile
): Required<
    Pick<LeaderboardPreferences, "optOut" | "nameFormat" | "displayName">
> {
    const stored = profile?.leaderboards;
    const nameFormat = stored?.nameFormat || DEFAULT_NAME_FORMAT;
    return {
        optOut: !!stored?.optOut,
        nameFormat,
        displayName:
            stored?.displayName || deriveDisplayName(profile, nameFormat),
    };
}

/**
 * Whether this user's matches should be submitted to venue boards.
 *
 * Absent preferences mean yes: the default is on. This is only the client's view — the server
 * re-reads and re-age-gates on every submission and is the authority — so it is an optimisation
 * that saves a pointless request, never the enforcement point.
 */
export function isParticipating(profile?: UserProfile): boolean {
    return !profile?.leaderboards?.optOut;
}

/**
 * Whether the profile is old enough for the boards, mirroring `meetsMinimumAge` in
 * `refauth/features/venues/VenueOptInService.ts` — including failing closed on an absent,
 * unparseable, future-dated or absurd `dob`.
 *
 * Only used to decide what the UI shows (the settings controls, and whether the one-time notice
 * is due). The server is the authority and re-checks on every submission, so the worst a
 * disagreement here can do — a timezone straddling a birthday — is show the notice a day early
 * or late.
 */
export function meetsMinimumAge(profile?: UserProfile, now?: Date): boolean {
    const raw = profile?.dob;
    if (!raw) {
        return false;
    }
    const dob = new Date(String(raw).trim());
    if (isNaN(dob.getTime())) {
        return false;
    }

    const at = now || new Date();
    let age = at.getUTCFullYear() - dob.getUTCFullYear();
    const monthDelta = at.getUTCMonth() - dob.getUTCMonth();
    if (
        monthDelta < 0 ||
        (monthDelta === 0 && at.getUTCDate() < dob.getUTCDate())
    ) {
        age -= 1;
    }
    if (age < 0 || age > 130) {
        return false;
    }
    return age >= MIN_LEADERBOARD_AGE_YEARS;
}

/**
 * Whether this user will actually appear on boards: old enough, has not opted out, and has a
 * name in their chosen format. The same three conditions the server checks in
 * `getParticipation`.
 */
export function isEligible(profile?: UserProfile, now?: Date): boolean {
    if (
        !profile ||
        !isParticipating(profile) ||
        !meetsMinimumAge(profile, now)
    ) {
        return false;
    }
    return !!getLeaderboardPreferences(profile).displayName;
}

/**
 * Classify a rejected profile write so the UI can explain what actually went wrong.
 *
 * The server refuses the whole PUT with a 400 and `{errorMessage: "leaderboards:<code>"}`.
 * Anything we cannot place — a plain network failure, a 500, a code added later — is "unknown",
 * because guessing "you are too young" at someone whose request merely timed out is worse than
 * a generic message.
 */
export function classifyOptInRejection(error: any): OptInRejection {
    const body = error?.response?.data ?? error?.data;
    const message =
        typeof body === "string"
            ? body
            : body?.errorMessage || body?.error || body?.message || "";
    const match = /^leaderboards:(.+)$/.exec(String(message).trim());

    if (!match) {
        return "unknown";
    }
    return REJECTION_CODES[match[1]] || "unknown";
}

/** The i18n key for a rejected change. Shared so every surface says the same thing. */
export function optInErrorKey(error: any): string {
    switch (classifyOptInRejection(error)) {
        case "ageGate":
            return "leaderboards.Settings.errorAgeGate";
        case "displayNameUnavailable":
            return "leaderboards.Settings.errorDisplayNameUnavailable";
        default:
            return "leaderboards.Settings.errorGeneric";
    }
}

/**
 * Persist a preference change through the existing profile endpoint. Rejects with the original
 * error so the caller can classify it — a change the server refuses must NOT be shown as success.
 *
 * We send only what the user chose. The server derives `displayName` from the format, stamps
 * `updatedAt`, ignores both if sent, and echoes the stored value back in the response — which
 * `updateProfile` pushes into redux, so no follow-up GET is needed. Omitting a field leaves that
 * side of the preferences as it was, so changing the name format cannot accidentally rejoin
 * someone who had left.
 */
export async function setLeaderboardPreferences(
    profile: UserProfile,
    change: LeaderboardPreferencesRequest
): Promise<UserProfile> {
    const leaderboards: LeaderboardPreferencesRequest = {};
    if (change.optOut !== undefined) {
        leaderboards.optOut = change.optOut;
    }
    if (change.nameFormat !== undefined) {
        leaderboards.nameFormat = change.nameFormat;
    }

    const response = await updateProfile(profile.username, {
        ...profile,
        leaderboards,
    });
    return response.data;
}
